import React from 'react';
import CopyButton from './CopyButton';
import Tooltip from './Tooltip';
import { TbSitemap } from 'react-icons/tb';
import { BUTTON_THEMES } from '../config/themes';

const CompartmentList = ({
  items = [],
  expandedState = {},
  setExpandedState,
  showOnButtonPress = false,
  buttonPressed = true,
  defaultExpanded = true,
  theme = 'emerald', // 'emerald' or 'terminal'
  icon = '📋', // icon or React component
  emptyMessage = '',
  loadingMessage = 'Waiting for content...',
  loading = false,
  showGraphButton = true // Show graph button by default
}) => {
  // Don't show if showOnButtonPress is true but button wasn't pressed
  if (showOnButtonPress && !buttonPressed) {
    return null;
  }

  // Theme configurations
  const themeConfig = {
    emerald: {
      containerClass: 'mb-3 bg-emerald-50 rounded-lg border border-emerald-200',
      headerClass: 'px-3 py-1.5 bg-emerald-100 border-b border-emerald-200 cursor-pointer hover:bg-emerald-200 flex items-center justify-between',
      arrowClass: 'text-emerald-600 text-sm font-mono',
      titleClass: 'font-semibold text-emerald-800 text-sm truncate',
      contentClass: 'p-3 bg-white',
      contentTextClass: 'text-gray-700 text-sm whitespace-pre-wrap',
      copyTheme: 'emerald'
    },
    terminal: {
      containerClass: 'mb-3 bg-black bg-opacity-30 rounded-lg border border-gray-600',
      headerClass: 'flex items-center justify-between px-3 py-1.5 cursor-pointer hover:bg-gray-800 hover:bg-opacity-50 transition-colors rounded-t-lg',
      arrowClass: 'text-green-400 text-sm font-mono',
      titleClass: 'text-green-300 font-medium text-sm truncate flex items-center',
      contentClass: 'border-t border-gray-600 p-3 bg-black bg-opacity-20',
      contentTextClass: 'text-green-400 text-sm whitespace-pre-wrap',
      copyTheme: 'terminal'
    }
  };

  const config = themeConfig[theme];


  if (items.length === 0) {
    if (loading) {
      return (
        <div className="text-gray-500 text-sm pt-2 italic">
          {loadingMessage}
        </div>
      );
    }
    return null;
  }

  return (
    <div className="pt-2">
      {items.map((item, index) => {
        // Safety checks
        if (!item) {
          console.warn(`Null item at index ${index}`);
          return null;
        }

        if (!item.id || typeof item.id !== 'string') {
          console.warn('Invalid item ID:', item);
          return null;
        }

        if (!item.title || typeof item.title !== 'string') {
          console.warn('Invalid item title:', item);
          return null;
        }

        const isExpanded = defaultExpanded
          ? expandedState[item.id] !== false
          : expandedState[item.id] === true;

        const toggleExpanded = () => {
          if (!item.id) return;
          setExpandedState(prev => {
            const currentState = defaultExpanded
              ? prev[item.id] !== false
              : prev[item.id] === true;
            return {
              ...prev,
              [item.id]: !currentState
            };
          });
        };

        return (
          <div key={item.id} className={config.containerClass}>
            {/* Clickable header */}
            <div className={config.headerClass} onClick={toggleExpanded}>
              <div className="flex items-center gap-2 min-w-0">
                <span className={config.arrowClass + ' flex items-center'} style={{fontSize: '12px'}}>
                  {isExpanded ? '▼' : '▶'}
                </span>
                <span className={config.titleClass + ' flex items-center'}>
                  {icon}<span>&nbsp;</span>{index + 1}. {item.title}
                </span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {showGraphButton && (
                  <Tooltip tooltip="View Knowledge Graph">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // TODO: Add graph functionality here
                        console.log('Graph icon clicked for:', item.id);
                      }}
                      className={`p-1.5 rounded transition-all ${BUTTON_THEMES[config.copyTheme]?.bg} ${BUTTON_THEMES[config.copyTheme]?.text} border ${BUTTON_THEMES[config.copyTheme]?.border} active:scale-90`}
                    >
                      <TbSitemap className="w-4 h-4" />
                    </button>
                  </Tooltip>
                )}
                <CopyButton
                  content={item.content || ''}
                  theme={config.copyTheme}
                />
              </div>
            </div>

            {/* Expandable content */}
            {isExpanded && (
              <div className={config.contentClass}>
                <pre className={config.contentTextClass}>
                  {item.content || emptyMessage}
                </pre>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CompartmentList;